import { useSelector } from 'react-redux';
import { Flex, Box, Tooltip, Text } from '@chakra-ui/react';
import { TField } from './Form';
import { allIcons, NormalIcon } from '../utils/Icon';
import { TState, useActions } from '../../redux/store';
import { useCanI, useGetSelect } from '../../redux/actions';
import { useEffect, useRef, useState } from 'react';

type TFormGroupProps = {
  field: TField;
  input?: JSX.Element;
  prefixLabel?: string;
  planable?: boolean;
  isError?: boolean;
  direction?: 'column' | 'row';
};

const FormGroup = ({
  field,
  input,
  prefixLabel,
  planable,
  isError,
  direction,
}: TFormGroupProps) => {
  const canI = useCanI();
  const { setModalType, setPlanningAddress } = useActions();
  const selectById = useGetSelect();

  const theme = useSelector((state: TState) => state.theme);
  const modalData = useSelector((state: TState) => state.modalData);

  const [isTroncated, setIsTroncated] = useState(false);
  const inputRef = useRef<any>();

  const isSwitch = /switch|checkbox|triple/.test(field.type || '');
  const isTitle = /title/.test(field.type || '');
  const isAddress = /address/.test(field.type || '');
  const isRow = direction === 'row';

  const labelFinal = `${prefixLabel ? `${prefixLabel} ` : ''}${
    isAddress && !!prefixLabel ? field.label : ''
  }${
    !isAddress
      ? (selectById(field.label || '')?.label || field.label)?.toLowerCase()
      : ''
  }`;

  useEffect(() => {
    const el = inputRef?.current;

    if (el && el.scrollWidth > el.clientWidth) {
      setIsTroncated(true);
    } else {
      setIsTroncated(false);
    }
  }, []);

  return (
    <Flex
      minH={isSwitch && !isRow ? '50px' : 'auto'}
      alignItems={isRow || isSwitch ? 'center' : 'flex-start'}
      w='100%'
      direction={isRow || isSwitch ? 'row' : 'column'}
      mt={isTitle ? 5 : 1}
      fontSize={field.type === 'title' ? '1.6em' : '1em'}
    >
      {(prefixLabel || (!isAddress && field.label)) && (
        <Flex
          textTransform='capitalize'
          m={0}
          mr={3}
          fontWeight='450'
          w={field.noFormat ? 'auto' : '100%'}
          justifyContent={isTitle ? 'center' : 'left'}
          alignItems='center'
          fontSize={isTitle ? theme.fontSize.bigger : theme.fontSize.small}
          flexGrow={field.noFormat ? 0 : 1}
          maxW='95%'
          gap={1}
        >
          <Tooltip
            label={isTroncated ? labelFinal : ''}
            textTransform='capitalize'
          >
            <Text
              ref={inputRef}
              as='span'
              overflow='hidden'
              whiteSpace='nowrap'
              textOverflow='ellipsis'
            >
              {labelFinal}
            </Text>
          </Tooltip>
          {field.required && (
            <Text color='red' ml={1} as='span'>
              *
            </Text>
          )}
          {field.advised && !field.required && (
            <Text color='orange.300' ml={1} as='span'>
              *
            </Text>
          )}
          {planable && canI('planning', 'create') && (
            <Tooltip
              label={`${
                modalData?.hasUpdates
                  ? 'Sauvegardez avant de pouvoir planifier un rdv'
                  : 'Fixer un rdv'
              }`}
              placement='right'
            >
              <span>
                <NormalIcon
                  disabled={modalData?.hasUpdates}
                  as={allIcons.HiCalendarDays}
                  boxSize='20px'
                  ml={1}
                  pb={0.5}
                  backgroundColor='none'
                  onClick={() => {
                    setPlanningAddress(field.name_id || '');
                    setModalType('addToPlanning');
                  }}
                />
              </span>
            </Tooltip>
          )}
        </Flex>
      )}
      {input ? (
        <Box
          w={isSwitch ? 'auto' : '100%'}
          textAlign='left'
          border={isError && (!isAddress || prefixLabel) ? '1px solid red' : ''}
          borderRadius='7px'
        >
          {input}
        </Box>
      ) : null}
    </Flex>
  );
};

export default FormGroup;
